import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import useAutoOpenAdd from "../common/useAutoOpenAdd";
import { Sparkle } from "../controls";
import { treatmentBand } from "../treatmentStatus";
import { formatShortDate } from "../chiefComplaints/dateHelpers";
import {
  recentEpisodeSinceWhen as _recentEpisodeSinceWhen,
  recentResolveSinceWhen as _recentResolveSinceWhen,
} from "./episodeDates";
import DiagnosisService from "./DiagnosisService";
import TrendModal from "./TrendModal";
import BulletListCell from "./BulletListCell";
import { fetchComplaints, fetchLabs, fetchMedicines } from "./linkedRefsData";
import { DX_V2_ADD, DX_V2_REFRESH, DX_V2_STATUS, DX_V2_EDIT_CAPTURED } from "./events";
import { MdChevronDown } from "../chiefComplaints/ccIcons";
import { ToolbarPlusIcon } from "../labInvestigations/labIcons";
import { StethoscopeIcon } from "./dxIcons";

/**
 * Diagnosis — content column, backed by api-diagnosis-v2-service. A single
 * fetched doc feeds the active + past tables; the trend modal stays a dialog.
 *
 * This column no longer writes anything. Both write surfaces live in the
 * editor rail (DiagnosisEditor): the toolbar's "+ Add diagnosis" fires
 * DX_V2_ADD, a row's treatment-status chip fires DX_V2_STATUS with that
 * record, and the rail emits DX_V2_REFRESH once the server confirms.
 */

// Trend button appears from the very first save. Every persisted
// diagnosis has at least one StatusEntry (the initial add row) and the
// reviewer wants to see that even before a second update lands — same
// rule as CC and Medicine v2.
const hasTrend = (d) => Array.isArray(d.statusHistory) && d.statusHistory.length >= 1;

// "Since when" for both the active and past diagnosis tables — start of
// the most recent episode segment. See ./episodeDates for the algorithm.
const recentEpisodeSinceWhen = (d) =>
  _recentEpisodeSinceWhen(
    d.statusHistory,
    (d.current && d.current.sinceWhen) || d.addedAt,
  );

// "Resolved on" for the past diagnosis table — patient-reported date from
// the audit entry that CLOSED the most recent episode, not the wall-clock
// d.resolvedAt.
const recentResolveSinceWhen = (d) =>
  _recentResolveSinceWhen(d.statusHistory, d.resolvedAt);

const TreatmentChip = ({ ongoing, onClick, disabled }) => {
  const band = treatmentBand(ongoing ? "ongoing" : "resolved");
  return (
    <button
      type="button"
      className="cc3-chip is-small dg3-chip"
      style={{ background: band.bg, color: band.fg }}
      onClick={onClick}
      disabled={disabled}
      title={disabled ? "Resolved — reopen by adding again" : "Update status"}
    >
      <span className="cc3-chip-dot" style={{ background: band.dot }} aria-hidden="true" />
      <span className="cc3-chip-label">{band.label}</span>
      <span className="cc3-chip-chev" aria-hidden="true"><MdChevronDown /></span>
    </button>
  );
};

TreatmentChip.propTypes = {
  ongoing: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// Map the AI's treatment_status enum ("ongoing"/"completed"/etc.) to the
// boolean `ongoing` that Dx v2 stores. Only "ongoing" is true; every
// other value ("completed", "scheduled", "none") is false. Kept in one
// place so add-time + update-time seeding stay in sync.
const ongoingFromStatus = (s) =>
  String(s || "").trim().toLowerCase() === "ongoing";

// Build a synthetic Dx-shaped row from a staged "add" captured item so
// it can slot into the same active / past tables + sort + render code
// as Dx v2 rows. `_captured` drives the peach background + sparkle in
// the row render. Mirrors chiefComplaints' `stagedAddToRow`.
const stagedAddToDxRow = (item) => {
  const p = (item && item.payload) || {};
  return {
    diagnosisId: `staged-${item.id}`,
    diagnosis:   p.diagnosis || p.name || p.value || "(untitled)",
    diagnosisCode: p.icd_code ? { icdCode: p.icd_code } : null,
    ongoing: ongoingFromStatus(p.treatment_status),
    addedAt: new Date().toISOString(),
    current: {
      sinceWhen:   p.since_date || p.since_when || null,
      description: p.description || "",
      consultedDoctor: !!p.consulted_doctor,
    },
    statusHistory: [],
    medicineRefs: [],
    _captured:      item,
    _capturedIsAi:  (item.confidence || 0) > 0,
    _stagedAdd:     true,
  };
};

const Diagnosis = ({ patient, capturedItems, autoOpenAdd, onAutoOpenAdd }) => {
  const patientId = patient && (patient.id || patient.patientID);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pastOpen, setPastOpen] = useState(false);
  const [trendTarget, setTrendTarget] = useState(null);

  // id -> label lookups so each row's linked ref arrays render as names.
  // Populated in parallel useEffects below; the resolvers `medicinesOf`,
  // `complaintsOf` and `labsOf` read these maps.
  const [medicineNames, setMedicineNames] = useState({});
  const [complaintNames, setComplaintNames] = useState({});
  const [labNames, setLabNames] = useState({});

  const load = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    const svc = new DiagnosisService();
    svc.list(
      patientId,
      (res) => {
        setDoc(res || { active: [], past: [], version: 0 });
        setLoading(false);
      },
      (err) => {
        setError((err && err.message) || "Failed to load diagnoses");
        setLoading(false);
      }
    );
  }, [patientId]);

  useEffect(() => {
    load();
    EventService.addListener(DX_V2_REFRESH, load);
    return () => EventService.removeListener(DX_V2_REFRESH, load);
  }, [load]);

  // Both forms live in the editor rail now (2961:4745) — add from the
  // toolbar, update from a row's treatment-status chip.
  //
  // Transcript-mode re-edit of a staged captured item fires a distinct
  // event so the sidebar's editor rail doesn't accidentally open an
  // "update Dx v2" sheet for a row that isn't in Dx v2 yet (staged-add)
  // or already has a pending staged update (dedup). Mirrors CC's
  // openUpdate / CC_V2_EDIT_CAPTURED split.
  const openAdd = () => EventService.emit(DX_V2_ADD);
  useAutoOpenAdd({ autoOpenAdd, onAutoOpenAdd, loading, error, doc, open: openAdd });
  const openStatus = (d) => {
    if (d && d._captured) {
      EventService.emit(DX_V2_EDIT_CAPTURED, {
        captured:    d._captured,
        dxv2Record:  d._stagedAdd ? null : d,
      });
      return;
    }
    EventService.emit(DX_V2_STATUS, { diagnosis: d });
  };

  useEffect(() => {
    if (!patientId) return undefined;
    let cancelled = false;
    // Parallel fetches. Complaints + labs feed the Description/Evidence
    // bullet cell (main table shows description + complaints + labs; past
    // table adds medicines to that list). Failures resolve to empty maps
    // in the helpers so a partial fetch never breaks the row render.
    fetchMedicines(patientId).then((list) => {
      if (cancelled) return;
      const map = {};
      (list || []).forEach((m) => { map[m.id] = m.label; });
      setMedicineNames(map);
    });
    fetchComplaints(patientId).then((list) => {
      if (cancelled) return;
      const map = {};
      (list || []).forEach((c) => { map[c.id] = c.label; });
      setComplaintNames(map);
    });
    fetchLabs(patientId).then((list) => {
      if (cancelled) return;
      const map = {};
      (list || []).forEach((l) => { map[l.id] = l.label; });
      setLabNames(map);
    });
    return () => { cancelled = true; };
  }, [patientId]);

  const activeRaw = (doc && doc.active) || [];
  const pastRaw = (doc && doc.past) || [];

  // Transcript-mode overlay. When `capturedItems` is unpassed / empty
  // (standalone sidebar renders), every derived list falls back to the
  // server's `active` / `past` unchanged. Mirrors ChiefComplaints'
  // capturedItems merge exactly:
  //   • operation="add"     state=added_in_call/edited_in_call →
  //                          new synthetic row slotted in.
  //   • operation="update"  targetEntryId=X → annotate the existing
  //                          row X. If the update flips `ongoing` the
  //                          row re-buckets into Active / Past.
  //   • confidence > 0      → sparkle icon on the row.
  const inTranscriptMode = !!(capturedItems && capturedItems.length > 0);
  const stagedForSection = (capturedItems || []).filter(
    (it) => it && (it.state === "added_in_call" || it.state === "edited_in_call"),
  );
  const updatesByEntryId = new Map();
  const addRows = [];
  for (const it of stagedForSection) {
    if (it.operation === "update" && it.targetEntryId) {
      updatesByEntryId.set(String(it.targetEntryId), it);
    } else if (it.operation === "add") {
      addRows.push(stagedAddToDxRow(it));
    }
  }

  // Overlay any staged update fields onto the row's `current` + `ongoing`
  // so the visible chip / description column reflects the staged edit
  // (not the untouched Dx v2 values). Same rationale as CC's
  // `withEffective` — the render reads `d.ongoing`, `d.current.*`
  // directly so we need to mutate those, not a shadow.
  const withEffectiveDx = (row) => {
    const update = row._captured;
    if (!update || update.operation !== "update") {
      return {
        ...row,
        _effectiveOngoing: !!row.ongoing,
        _effectiveResolvedAt: row.resolvedAt,
      };
    }
    const p = (update.payload) || {};
    const cur = row.current || {};
    const ongoing = p.treatment_status !== undefined
      ? ongoingFromStatus(p.treatment_status)
      : !!row.ongoing;
    // Mirrors CC's staged-resolve handling — when a captured update flips
    // this row to resolved, prefer the payload's patient-reported date
    // over the stored wall-clock resolvedAt (which is null on a first-
    // time staged resolve).
    const resolvedAt = ongoing === false
      ? (p.since_when || row.resolvedAt || new Date().toISOString())
      : row.resolvedAt;
    return {
      ...row,
      ongoing,
      current: {
        ...cur,
        sinceWhen:       p.since_when || cur.sinceWhen,
        description:     p.description !== undefined ? p.description : cur.description,
        consultedDoctor: typeof p.consulted_doctor === "boolean"
                            ? p.consulted_doctor
                            : cur.consultedDoctor,
      },
      _effectiveOngoing: ongoing,
      _effectiveResolvedAt: resolvedAt,
    };
  };
  const annotateWithUpdateDx = (row) => {
    const update = updatesByEntryId.get(String(row.diagnosisId));
    if (!update) return row;
    return { ...row, _captured: update, _capturedIsAi: (update.confidence || 0) > 0 };
  };

  let active;
  let past;
  if (inTranscriptMode) {
    const annotatedActive = activeRaw.map(annotateWithUpdateDx).map(withEffectiveDx);
    const annotatedPast   = pastRaw.map(annotateWithUpdateDx).map(withEffectiveDx);
    const stagedAddRows   = addRows.map(withEffectiveDx);
    const merged = [...annotatedActive, ...annotatedPast, ...stagedAddRows];
    active = merged.filter((r) => r._effectiveOngoing === true);
    past   = merged.filter((r) => r._effectiveOngoing === false);
  } else {
    active = activeRaw;
    past = pastRaw;
  }

  if (!patientId) {
    return <div className="ph-slot-empty"><p>Waiting for patient…</p></div>;
  }
  if (loading && !doc) {
    return <div className="ph-slot-empty"><p>Loading diagnoses…</p></div>;
  }
  if (error) {
    return (
      <div className="ph-slot-empty">
        <p>{error}</p>
        <button type="button" className="sec-btn sec-btn-ghost" onClick={load}>Retry</button>
      </div>
    );
  }

  // ── ref resolvers ───────────────────────────────────────────────────
  // Common shape across the three lookups: each Dx carries an ObjectId
  // array on `d.current.<field>`; the corresponding *Names map resolves
  // each id to its human label. Unresolved ids yield null so the bullet
  // list skips them cleanly (no "#abcdef" hex placeholders in the UI).
  const refsToLabels = (refs, nameMap) =>
    (refs || []).map((r) => {
      const id = typeof r === "object" ? r.id || String(r) : String(r);
      return nameMap[id] || null;
    });
  const complaintsOf = (d) =>
    refsToLabels(d.current && d.current.complaintIds, complaintNames);
  const labsOf = (d) =>
    refsToLabels(d.current && d.current.labRefs, labNames);
  const medicinesOf = (d) => {
    // Dx v2 stores linked medicine ids on the *current-state* snapshot
    // (`d.current.medicineRefs`), NOT at the top level. Reading `d.medicineRefs`
    // always yielded undefined so every row rendered empty. Falling back to the
    // top-level covers legacy shapes if any survive; the primary lookup is
    // `d.current.medicineRefs`.
    const refs =
      (d.current && d.current.medicineRefs) ||
      d.medicineRefs ||
      [];
    return refs.map((r) => {
      const id = typeof r === "object" ? r.id || r.medicineId : r;
      return medicineNames[id] || null;
    });
  };
  // Description/Evidence composition for the ONGOING table: description
  // (when non-blank) as the first bullet, then linked chief complaints,
  // then linked lab values. Medicines stay in their own column here.
  const evidenceLinksOf = (d) => {
    const items = [];
    const desc = ((d.current && d.current.description) || "").trim();
    if (desc) items.push(desc);
    for (const c of complaintsOf(d)) if (c) items.push(c);
    for (const l of labsOf(d)) if (l) items.push(l);
    return items;
  };
  // Description/Evidence composition for the PAST table: same as above
  // plus medicines appended, since the past table has no Medicines column.
  const evidenceAndMedsOf = (d) => {
    const items = evidenceLinksOf(d);
    for (const m of medicinesOf(d)) if (m) items.push(m);
    return items;
  };

  // Two empty shapes:
  //   1. no active AND no past → fresh patient, "No diagnosis recorded"
  //   2. no active BUT past exists → everything resolved, "No active diagnoses"
  // Header `+ Add` hides in both cases — the empty-state CTA takes over so
  // the reviewer's next action stays visible. Mirrors ChiefComplaints.
  const hasActive = active.length > 0;
  const hasPast = past.length > 0;
  const showHeaderAdd = hasActive;
  const emptyCopy = hasPast
    ? {
        title: "No active diagnoses",
        hint: "Everything here is resolved — resolved diagnoses live in Past diagnoses below. Add a diagnosis when a new one is identified.",
        countLabel: "No active",
      }
    : {
        title: "No diagnosis recorded",
        hint: "Nothing has been logged yet. Add the patient's first diagnosis to begin building their history.",
        countLabel: "No entries",
      };

  return (
    <div className="cc3-wrap">
      <div className="cc3-card">
        {/* Same toolbar as Chief complaints — the sections read as one family. */}
        <header className="cc3-toolbar">
          <h2 className="cc3-title">Diagnosis</h2>
          <span className="cc3-count">
            &middot;{" "}
            {hasActive
              ? `${active.length} ${active.length === 1 ? "entry" : "entries"}`
              : emptyCopy.countLabel}
          </span>
          {showHeaderAdd && (
            <button type="button" className="cc3-add" onClick={openAdd}>
              <ToolbarPlusIcon /> Add diagnosis
            </button>
          )}
        </header>

        {!hasActive ? (
          <div className="cc-empty">
            <span className="cc-empty-medallion" aria-hidden="true"><StethoscopeIcon /></span>
            <p className="cc-empty-title">{emptyCopy.title}</p>
            <p className="cc-empty-hint">{emptyCopy.hint}</p>
            <button type="button" className="cc-empty-cta" onClick={openAdd}>
              <ToolbarPlusIcon /> Add diagnosis
            </button>
          </div>
        ) : (
          <div className="dg3-table" role="table">
            <div className="dg3-thead" role="row">
              <span role="columnheader">Diagnosis</span>
              <span role="columnheader">Since when</span>
              <span role="columnheader">Description/Evidence</span>
              <span role="columnheader">Medicines</span>
              <span role="columnheader">Treatment status</span>
              <span role="columnheader">Analysis</span>
            </div>

            {active.map((d) => (
              <div
                className={`dg3-row${d._captured ? " is-captured" : ""}`}
                role="row"
                key={d.diagnosisId}
              >
                <span className="dg3-cell dg3-c-name" role="cell">
                  {d._capturedIsAi ? (
                    <span className="cc-spark" aria-label="AI captured" title="AI captured"><Sparkle aria-hidden /></span>
                  ) : null}
                  {/* Diagnosis name is a button — same handler as the
                      Treatment-status chip (openStatus). Mirrors
                      Hospitalisation's "Cause of hospitalisation"
                      column. */}
                  <button
                    type="button"
                    className="dg3-name-btn dg3-name-txt"
                    onClick={() => openStatus(d)}
                    title="Update diagnosis"
                  >
                    {d.diagnosis}
                    {d.diagnosisCode && d.diagnosisCode.icdCode ? (
                      <span className="cc-icd-inline"> &middot; ICD {d.diagnosisCode.icdCode}</span>
                    ) : null}
                  </button>
                </span>
                <span className="dg3-cell dg3-c-since" role="cell">
                  {formatShortDate(recentEpisodeSinceWhen(d))}
                </span>
                <span className="dg3-cell dg3-c-bullets" role="cell">
                  <BulletListCell items={evidenceLinksOf(d)} />
                </span>
                <span className="dg3-cell is-tight dg3-c-bullets" role="cell">
                  <BulletListCell items={medicinesOf(d)} />
                </span>
                <span className="dg3-cell" role="cell">
                  <TreatmentChip
                    ongoing={!!d.ongoing}
                    onClick={() => openStatus(d)}
                  />
                </span>
                <span className="dg3-cell is-tight" role="cell">
                  {hasTrend(d) ? (
                    <button
                      type="button"
                      className="cc3-trend dg3-trend"
                      onClick={() => setTrendTarget(d)}
                    >
                      View trend
                    </button>
                  ) : (
                    <span className="cc-dash">—</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Past diagnosis: dashed band collapsed (2961:4740); card when open. */}
      {!pastOpen ? (
        <div className="cc3-past">
          <button
            type="button"
            className="cc3-past-toggle"
            aria-expanded={false}
            onClick={() => setPastOpen(true)}
          >
            <span className="cc3-past-title">Past diagnosis</span>
            <span className="cc3-past-count">
              {past.length} resolved {past.length === 1 ? "entry" : "entries"}
            </span>
            <span className="cc3-past-chev" aria-hidden="true">
              <MdChevronDown />
            </span>
          </button>
        </div>
      ) : (
        <div className="cc3-card">
          <button
            type="button"
            className="cc3-past-cardbar"
            aria-expanded
            onClick={() => setPastOpen(false)}
          >
            <span className="cc3-past-title">Past diagnosis</span>
            <span className="cc3-past-count">
              {past.length} resolved {past.length === 1 ? "entry" : "entries"}
            </span>
            <span className="cc3-past-chev is-open" aria-hidden="true">
              <MdChevronDown />
            </span>
          </button>

          {past.length > 0 && (
            <div className="dg3-table is-past" role="table">
              <div className="dg3-thead" role="row">
                <span role="columnheader">Diagnosis</span>
                <span role="columnheader">Since when</span>
                <span role="columnheader">Description/Evidence</span>
                <span role="columnheader">Resolved on</span>
                <span role="columnheader">Treatment status</span>
                <span role="columnheader">Analysis</span>
              </div>

              {past.map((d) => (
                <div
                  className={`dg3-row${d._captured ? " is-captured" : ""}`}
                  role="row"
                  key={d.diagnosisId}
                >
                  <span className="dg3-cell dg3-c-name" role="cell">
                    {d._capturedIsAi ? (
                      <span className="cc-spark" aria-label="AI captured" title="AI captured"><Sparkle aria-hidden /></span>
                    ) : null}
                    {/* Diagnosis name is a button — same handler as the
                        Treatment-status chip. See active-table sibling. */}
                    <button
                      type="button"
                      className="dg3-name-btn dg3-name-txt"
                      onClick={() => openStatus(d)}
                      title="Update diagnosis"
                    >
                      {d.diagnosis}
                      {d.diagnosisCode && d.diagnosisCode.icdCode ? (
                        <span className="cc-icd-inline"> &middot; ICD {d.diagnosisCode.icdCode}</span>
                      ) : null}
                    </button>
                  </span>
                  <span className="dg3-cell dg3-c-since" role="cell">
                    {formatShortDate(recentEpisodeSinceWhen(d))}
                  </span>
                  <span className="dg3-cell dg3-c-bullets" role="cell">
                    <BulletListCell items={evidenceAndMedsOf(d)} />
                  </span>
                  <span className="dg3-cell dg3-c-since" role="cell">
                    {formatShortDate(
                      // Staged AI-transcript resolve wins (its `since_when` is
                      // the patient-reported date on the pending update);
                      // otherwise pull the sinceWhen off the audit's most
                      // recent resolve entry instead of wall-clock resolvedAt.
                      (d._captured && d._effectiveOngoing === false && d._effectiveResolvedAt)
                      || recentResolveSinceWhen(d)
                    )}
                  </span>
                  <span className="dg3-cell" role="cell">
                    <TreatmentChip
                      ongoing={!!d.ongoing}
                      onClick={() => openStatus(d)}
                    />
                  </span>
                  <span className="dg3-cell is-tight" role="cell">
                    {hasTrend(d) ? (
                      <button
                        type="button"
                        className="cc3-trend dg3-trend"
                        onClick={() => setTrendTarget(d)}
                      >
                        View trend
                      </button>
                    ) : (
                      <span className="cc-dash">—</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {trendTarget && (
        <TrendModal
          patientId={patientId}
          diagnosis={trendTarget}
          onClose={() => setTrendTarget(null)}
        />
      )}
    </div>
  );
};

Diagnosis.propTypes = {
  /** Open the add form once the list loads — Create Rx's "+ Add …" (useAutoOpenAdd). */
  autoOpenAdd: PropTypes.bool,
  onAutoOpenAdd: PropTypes.func,
  patient: PropTypes.object,
  /**
   * Transcript-mode overlay. When populated, staged captured items for
   * `chief_complaints`… wait, this is Diagnosis — for `diagnosis` — are
   * merged into the active / past tables (peach highlight + sparkle for
   * AI-touched rows). Empty / unpassed = standalone behavior, table
   * shows only Dx v2 server data.
   */
  capturedItems: PropTypes.array,
};

Diagnosis.defaultProps = {
  capturedItems: [],
};

export default Diagnosis;
